import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";
import { PencilIcon, SearchIcon, TrashIcon } from "lucide-react";
import { EditCustomerDialog } from "./EditCustomerDialog";
import { AuditCell } from "./AuditCell";
import { ConfirmActionDialog } from "./ConfirmActionDialog";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { cn } from "@/lib/utils";

interface CustomerRow {
  id: string;
  organisationId: string;
  name: string;
  email: string | null;
  phone: string | null;
  taxNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

interface CustomerSearchHit {
  id: string;
  name: string;
  email: string | null;
  score: number;
}

interface CustomersTableProps {
  organisationId: string;
}

export function CustomersTable({ organisationId }: CustomersTableProps) {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [editing, setEditing] = useState<CustomerRow | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomerRow | null>(null);

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 400);
  const [hits, setHits] = useState<CustomerSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const pagination = useCursorPagination({ initialLimit: 20 });
  const { cursor, limit, setPage, reset } = pagination;

  // Reset the cursor during render, not in an effect: an effect would send the stale cursor once, and a
  // cursor only means something within one organisation's list.
  const [cursorOrganisation, setCursorOrganisation] = useState(organisationId);
  if (cursorOrganisation !== organisationId) {
    setCursorOrganisation(organisationId);
    reset();
  }

  // Only the newest fetch may write: page size stays enabled mid-flight, so an older answer can land last.
  const requestId = useRef(0);

  const fetchCustomers = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await api.GET("/v1/customers", {
      params: { query: { Cursor: cursor, Limit: limit } },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (id !== requestId.current) return;

    if (apiError) {
      setError("Failed to load customers.");
    } else {
      setCustomers(((data as any)?.data as CustomerRow[]) ?? []);
      setPage({
        nextCursor: (data as any)?.meta?.cursors?.next,
        previousCursor: (data as any)?.meta?.cursors?.previous,
      });
    }
    setLoading(false);
  }, [organisationId, cursor, limit, setPage]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q) {
      setHits([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    (async () => {
      const { data, error: apiError } = await api.GET("/v1/customers/search", {
        params: { query: { q, limit: 10 } },
        headers: { "x-dolfin-organisation-id": organisationId },
      });
      if (cancelled) return;
      if (!apiError && data) {
        const sorted = [...(data as CustomerSearchHit[])].sort(
          (a, b) => Number(b.score ?? 0) - Number(a.score ?? 0),
        );
        setHits(sorted);
      } else {
        setHits([]);
      }
      setSearching(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, organisationId]);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // A hit is usually outside the loaded page, so fall back to the detail page.
  function handleSelectHit(id: string) {
    setShowResults(false);
    const row = rowRefs.current.get(id);
    if (!row) {
      navigate(`/customers/${id}?organisationId=${organisationId}`);
      return;
    }
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedId(id);
    window.setTimeout(() => {
      setHighlightedId((cur) => (cur === id ? null : cur));
    }, 2000);
  }

  async function handleDelete(id: string) {
    setDeleting(id);
    const { error: apiError } = await api.DELETE("/v1/customers/{id}", {
      params: { path: { id } },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (apiError) {
      setError("Failed to delete customer. It may have associated invoices.");
    } else {
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    }
    setDeleting(null);
    setDeleteTarget(null);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Customers</h2>
          {loading && <Spinner className="size-5" />}
        </div>
        <div ref={searchRef} className="relative w-72">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            type="search"
            placeholder="Search customers..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            className="pl-8"
          />
          {showResults && debouncedQuery.trim() && (
            <div className="bg-popover text-popover-foreground absolute top-full right-0 left-0 z-20 mt-1 max-h-80 overflow-auto rounded-md border shadow-md">
              {searching ? (
                <div className="text-muted-foreground flex items-center justify-center gap-2 p-3 text-sm">
                  <Spinner className="size-4" /> Searching…
                </div>
              ) : hits.length === 0 ? (
                <div className="text-muted-foreground p-3 text-sm">No matches.</div>
              ) : (
                <ul className="py-1">
                  {hits.map((hit) => (
                    <li key={hit.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectHit(hit.id)}
                        className="hover:bg-accent hover:text-accent-foreground w-full px-3 py-2 text-left text-sm"
                      >
                        <div className="truncate font-medium">{hit.name}</div>
                        {hit.email && (
                          <div className="text-muted-foreground truncate text-xs">
                            {hit.email}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-destructive mb-4 text-sm">{error}</p>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Last modified</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  No customers found.
                </TableCell>
              </TableRow>
            ) : (
              customers.map((c) => (
                <TableRow
                  key={c.id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(c.id, el);
                    else rowRefs.current.delete(c.id);
                  }}
                  className={cn(
                    "transition-colors duration-500",
                    highlightedId === c.id && "bg-accent",
                  )}
                >
                  <TableCell className="font-medium">
                    <Link
                      to={`/customers/${c.id}?organisationId=${organisationId}`}
                      className="hover:underline"
                    >
                      {c.name}
                    </Link>
                  </TableCell>
                  <TableCell>{c.email || "—"}</TableCell>
                  <TableCell>{c.phone || "—"}</TableCell>
                  <TableCell>{c.country || "—"}</TableCell>
                  <TableCell>
                    <AuditCell
                      createdAt={c.createdAt}
                      createdBy={c.createdBy}
                      updatedAt={c.updatedAt}
                      updatedBy={c.updatedBy}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => setEditing(c)}
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={deleting === c.id}
                        onClick={() => setDeleteTarget(c)}
                      >
                        {deleting === c.id ? (
                          <Spinner className="size-4" />
                        ) : (
                          <TrashIcon className="size-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <EditCustomerDialog
        organisationId={organisationId}
        customer={editing}
        open={editing !== null}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        onUpdated={fetchCustomers}
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete this customer?"
        description={
          <>Deleting <span className="font-medium">{deleteTarget?.name}</span> cannot be undone. If they have associated invoices, the delete will fail.</>
        }
        confirmLabel="Delete"
        pending={!!deleting}
        onConfirm={() => { if (deleteTarget) handleDelete(deleteTarget.id); }}
      />
    </div>
  );
}
